import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { ScreenData, getPrayerPageDuration } from "../../types/ScreenTypes";
import { PrayerTimes } from "../../types/PrayerTypes";
import { ScheduleType } from "../../types/index";
import {
  fetchScreenData,
  fetchPrayerTimes,
  isMissingTimetableFallback,
} from "../../services/api";
import CustomDatabaseLayoutDisplay from "../layouts/CustomDatabaseLayoutDisplay";
import ResponsiveDisplay from "../layouts/ResponsiveDisplay";
import LoadingSpinner from "../elements/LoadingSpinner";
import ErrorDisplay from "../elements/ErrorDisplay";
import FullscreenCountdownTakeover from "../elements/FullscreenCountdownTakeover";
import { startPrayerTimeSync } from "../../services/startPrayerTimeSync";
import { timetableDB } from "../../db/indexedDb";
import AuthMenu from "../Auth/AuthMenu";
import { isHomeCode } from "../../utils/homeScreen";
import { getMasjidId } from "../../services/authService";
import SlideshowPage from "../slideshow/SlideshowPage"; // New component we'll create
import CriticalSlideshowPage from "../slideshow/CriticalSlideshowPage"; // Critical playlist component
import AfterPrayerSalahChangesOverlay from "../slideshow/AfterPrayerSalahChangesOverlay";
import { useEnhancedPlaylistScheduler } from "../../hooks/useEnhancedPlaylistScheduler";
import { usePrayerStateManager } from "../../hooks/usePrayerStateManager";
import { useAfterPrayerSalahChanges } from "../../hooks/useAfterPrayerSalahChanges";
import { useFlashSalahChanges } from "../../hooks/useFlashSalahChanges";
import { useConnectionQuality } from "../../hooks/useConnectionQuality";
import PoweredBy from "../shared/PoweredBy";
import NoticeboardDisplay from "../noticeboard/NoticeboardDisplay";
import { getRotatedItems } from "../../utils/rotationUtils";
import { fetchNext7DaysPrayerTimes } from "../../services/api";
import { getUpcomingSalahLookaheadDays } from "../../types/ScreenTypes";
import { isNightModeActive } from "../../utils/prayerTimeUtils";
import { prefetchScreenAssets } from "../../utils/assetPrefetcher";
import { hasUpcomingSalahChanges } from "../../utils/upcomingSalahChangesEligibility";
import useLabbaikItems from "../../hooks/useLabbaikItems";

// Azure Best Practice: Define page states as a type
type PageState = "prayer" | "slideshow" | "changes" | "events" | "countdown";

// Define type for prayer times update event
interface PrayerTimesUpdateEvent extends CustomEvent {
  detail: {
    timestamp: string;
    succeeded?: number;
    masjidId?: string;
    year?: string;
    month?: string;
  };
}

interface DisplayControllerProps {
  screenCode: string;
}

function isUrgentWindowActive(playlist: any, now: Date): boolean {
  if (!playlist?.urgentActiveUntilUtc) {
    return false;
  }

  const from = playlist?.urgentActiveFromUtc
    ? new Date(playlist.urgentActiveFromUtc)
    : null;
  const until = playlist?.urgentActiveUntilUtc
    ? new Date(playlist.urgentActiveUntilUtc)
    : null;

  if (from && Number.isNaN(from.getTime())) {
    return false;
  }

  if (until && Number.isNaN(until.getTime())) {
    return false;
  }

  if (from && now < from) {
    return false;
  }

  if (until && now > until) {
    return false;
  }

  return true;
}

function isFullScreenUrgentRenderMode(playlist: any): boolean {
  // Backward compatibility: missing value means full-screen takeover.
  return playlist?.urgentRenderMode !== "RespectLayout";
}

function isUrgentTakeoverFullScreenActive(playlist: any, now: Date): boolean {
  return (
    playlist?.urgentMode === "UrgentTakeover" &&
    isFullScreenUrgentRenderMode(playlist) &&
    isUrgentWindowActive(playlist, now)
  );
}

function isUrgentInjectFullScreenActive(playlist: any, now: Date): boolean {
  return (
    playlist?.urgentMode === "UrgentInject" &&
    isFullScreenUrgentRenderMode(playlist) &&
    isUrgentWindowActive(playlist, now)
  );
}

// Define proper types for scheduler results
interface SchedulerPlaylist {
  id: string | number;
  name: string;
  priority?: number;
  items: any[];
  // Add other relevant fields
}

interface ActivePlaylistItem {
  playlist: SchedulerPlaylist;
  effectivePriority: number;
  reason: string;
}

interface SchedulerResult {
  activePlaylists: ActivePlaylistItem[];
  conflictsResolved: any[];
  // Add other fields
}

export default function DisplayController({
  screenCode,
}: DisplayControllerProps) {
  // Household tablets have their own settings on /home, so no hidden gear menu there.
  const authMenu = isHomeCode(screenCode) ? null : <AuthMenu />;

  // State management
  const [currentPage, setCurrentPage] = useState<PageState>("prayer");
  const [screenData, setScreenData] = useState<ScreenData | null>(null);
  // Labbaik slides count as slideshow content, so a screen with no playlists still shows them
  const hasLabbaik =
    useLabbaikItems(screenData?.masjidId, screenData?.showLabbaik).length > 0;
  const [prayerData, setPrayerData] = useState({
    today: null as PrayerTimes | null,
    tomorrow: null as PrayerTimes | null,
    prevFridayJummah1: null as string | null,
    prevFridayJummah2: null as string | null,
    prevFridayJummah3: null as string | null,
    prevFridayJummah4: null as string | null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadError, setLastLoadError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [transitionsDisabled, setTransitionsDisabled] = useState(false);
  const previousCriticalActiveRef = useRef(false);

  // Transform scheduleType from string to enum value for prayer state manager
  const transformedPlaylists = useMemo(
    () =>
      (screenData?.playlists || []).map((p: any) => ({
        ...p,
        scheduleType:
          typeof p.scheduleType === "string"
            ? ScheduleType[
                p.scheduleType as unknown as keyof typeof ScheduleType
              ]
            : p.scheduleType,
        // ✅ Transform playlist items to include nested asset object
        items: (p.items || []).map((item: any) => ({
          ...item,
          // Create nested asset object from flat backend properties
          asset: item.asset || {
            id: item.assetId,
            name: item.assetTitle || "",
            title: item.assetTitle || "",
            type: item.assetType?.toLowerCase() || "",
            url: item.assetUrl || "",
            thumbnailUrl: item.thumbnailUrl,
            duration: item.duration || 30, // Changed order here
            videoType: item.videoType,
            youtubeVideoId: item.youtubeVideoId,
            youtubePlaylistId: item.youtubePlaylistId,
            isVideoPlaylist: item.isVideoPlaylist,
            autoplay: item.videoAutoplay,
            muted: item.videoMuted,
            loop: item.videoLoop,
          },
        })),
      })),
    [screenData?.playlists],
  );

  // Get active playlists from enhanced scheduler for critical playlist detection
  const {
    schedulerResult,
    isLoading: schedulerLoading,
    error: schedulerError,
    evaluatePlaylists, // Extract this method from the hook result
  } = useEnhancedPlaylistScheduler({
    playlists: transformedPlaylists as any,
    prayerTimes: prayerData.today || undefined,
    screenData,
    enableAdvancedScheduling: true,
    configuration: {
      enablePriorityOverride: true,
      enableInterruption: false,
      evaluationInterval: 2000,
      enableLogging: process.env.NODE_ENV === "development",
    },
  });

  // ✅ Extract actual playlists from scheduler result wrapper objects
  const activeScheduledPlaylists = useMemo(() => {
    if (!schedulerResult?.activePlaylists) return [];

    // Extract the playlist from the wrapper { playlist: {...}, reason: "..." }
    const extracted = schedulerResult.activePlaylists.map((ap: any) => {
      const playlist = ap.playlist || ap;

      return playlist;
    });

    return extracted;
  }, [schedulerResult?.activePlaylists]);

  // Aggregate all items from active scheduled playlists for noticeboard display
  // ✅ Apply daily rotation per-playlist (matching SimpleNoticeboardDisplay behaviour)
  const allPlaylistItems = useMemo(() => {
    if (!activeScheduledPlaylists || activeScheduledPlaylists.length === 0)
      return [];
    return activeScheduledPlaylists.flatMap((playlist: any) => {
      let playlistItems = playlist.items || [];
      if (playlist.isDailyRotation && playlistItems.length > 0) {
        playlistItems = getRotatedItems(
          playlist,
          playlistItems
            .slice()
            .sort(
              (a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0),
            ),
          prayerData.today,
          "[DisplayController/Noticeboard]",
        );
      }
      return playlistItems;
    });
  }, [
    activeScheduledPlaylists.length,
    activeScheduledPlaylists.map((p: any) => p.items?.length || 0).join(","),
    prayerData.today?.GregorianDate, // re-evaluate when date changes
  ]);

  // Add the prayer state hook inside the component body - this fixes the invalid hook call
  const prayerState = usePrayerStateManager(
    screenData,
    prayerData.today,
    prayerData.tomorrow,
    transformedPlaylists as any, // All playlists for reference
    activeScheduledPlaylists as any, // ✅ Now contains actual playlist objects
    evaluatePlaylists, // Pass the function here
  );

  // ── After-Prayer Salah Changes: 7-day timetable fetch ──
  const [afterPrayerTimetables, setAfterPrayerTimetables] = useState<
    PrayerTimes[]
  >([]);
  const [upcomingSalahTimetables, setUpcomingSalahTimetables] = useState<
    PrayerTimes[]
  >([]);

  useEffect(() => {
    if (!screenData?.showSalahChangesAfterPrayer || !screenData?.masjidId) {
      setAfterPrayerTimetables([]);
      return;
    }
    let cancelled = false;
    const fetchTimetables = async () => {
      try {
        const lookaheadDays = getUpcomingSalahLookaheadDays(screenData);
        const data = await fetchNext7DaysPrayerTimes(
          screenData.masjidId,
          lookaheadDays,
        );
        if (!cancelled) setAfterPrayerTimetables(data);
      } catch (error) {
        console.error("Error fetching after-prayer timetables:", error);
        if (!cancelled) setAfterPrayerTimetables([]);
      }
    };
    fetchTimetables();
    return () => {
      cancelled = true;
    };
  }, [screenData?.showSalahChangesAfterPrayer, screenData?.masjidId]);

  // ── Upcoming Salah Changes: timetable fetch for slideshow/noticeboard eligibility ──
  useEffect(() => {
    if (!screenData?.showNextSalahChanges || !screenData?.masjidId) {
      setUpcomingSalahTimetables([]);
      return;
    }

    let cancelled = false;

    const fetchTimetables = async () => {
      try {
        const lookaheadDays = getUpcomingSalahLookaheadDays(screenData);
        const data = await fetchNext7DaysPrayerTimes(
          screenData.masjidId,
          lookaheadDays,
        );
        if (!cancelled) {
          setUpcomingSalahTimetables(data);
        }
      } catch (error) {
        console.error("Error fetching upcoming salah timetables:", error);
        if (!cancelled) {
          setUpcomingSalahTimetables([]);
        }
      }
    };

    fetchTimetables();

    return () => {
      cancelled = true;
    };
  }, [
    screenData?.showNextSalahChanges,
    screenData?.hideUpcomingSalahWhenNoChanges,
    screenData?.masjidId,
    screenData?.upcomingSalahLookaheadDays,
  ]);

  const upcomingSalahChangesEligible = useMemo(() => {
    if (!screenData?.showNextSalahChanges) {
      return false;
    }

    if (!screenData.hideUpcomingSalahWhenNoChanges) {
      return true;
    }

    return hasUpcomingSalahChanges({
      timetables: upcomingSalahTimetables,
      prayerTimes: prayerData.today,
      prevFridayJummah1: prayerData.prevFridayJummah1,
      prevFridayJummah2: prayerData.prevFridayJummah2,
      prevFridayJummah3: prayerData.prevFridayJummah3,
      prevFridayJummah4: prayerData.prevFridayJummah4,
    });
  }, [
    screenData?.showNextSalahChanges,
    screenData?.hideUpcomingSalahWhenNoChanges,
    upcomingSalahTimetables,
    prayerData.today,
    prayerData.prevFridayJummah1,
    prayerData.prevFridayJummah2,
    prayerData.prevFridayJummah3,
    prayerData.prevFridayJummah4,
  ]);

  // ── After-Prayer Salah Changes: detection hook ──
  const afterPrayerChanges = useAfterPrayerSalahChanges(
    prayerData.today,
    prayerData.tomorrow,
    screenData,
    !!screenData?.showSalahChangesAfterPrayer,
  );

  const connectionQuality = useConnectionQuality();

  // ── Flash Salah Changes: detection hook ──
  const flashSalahChanges = useFlashSalahChanges(
    prayerData.today,
    prayerData.tomorrow,
    screenData,
    !!screenData?.flashSalahChangesAfterPrayer,
  );

  // ── After-Prayer Salah Changes: dismiss/sequencing state (position-independent) ──
  const [afterPrayerOverlayDismissed, setAfterPrayerOverlayDismissed] =
    useState(false);
  const afterPrayerOverlayDismissedForRef = useRef<string | null>(null);
  const [
    criticalCompletedDuringAfterPrayer,
    setCriticalCompletedDuringAfterPrayer,
  ] = useState(false);
  const [latchedBeforeCriticalPlaylist, setLatchedBeforeCriticalPlaylist] =
    useState<any | null>(null);
  const [playLatchedBeforeCritical, setPlayLatchedBeforeCritical] =
    useState(false);
  const [queuedAfterCriticalOverlay, setQueuedAfterCriticalOverlay] =
    useState(false);
  const [queuedAfterCriticalPrayer, setQueuedAfterCriticalPrayer] = useState<
    string | null
  >(null);
  const criticalCompletedForRef = useRef<string | null>(null);
  const latchedBeforeCriticalForRef = useRef<string | null>(null);
  const queuedAfterCriticalForRef = useRef<string | null>(null);
  const afterPrayerDebugSignatureRef = useRef<string>("");

  // Reset dismissed state when the active prayer changes (new prayer window)
  useEffect(() => {
    if (
      afterPrayerChanges.activePrayer !==
      afterPrayerOverlayDismissedForRef.current
    ) {
      setAfterPrayerOverlayDismissed(false);
      afterPrayerOverlayDismissedForRef.current = null;
    }
    if (afterPrayerChanges.activePrayer !== criticalCompletedForRef.current) {
      setCriticalCompletedDuringAfterPrayer(false);
      criticalCompletedForRef.current = null;
    }

    if (
      afterPrayerChanges.activePrayer &&
      afterPrayerChanges.activePrayer !== latchedBeforeCriticalForRef.current
    ) {
      setLatchedBeforeCriticalPlaylist(null);
      setPlayLatchedBeforeCritical(false);
      latchedBeforeCriticalForRef.current = null;
    }

    if (
      afterPrayerChanges.activePrayer &&
      afterPrayerChanges.activePrayer !== queuedAfterCriticalForRef.current
    ) {
      setQueuedAfterCriticalOverlay(false);
      setQueuedAfterCriticalPrayer(null);
      queuedAfterCriticalForRef.current = null;
    }
  }, [afterPrayerChanges.activePrayer]);

  const canRenderAfterPrayerOverlay = useMemo(() => {
    return afterPrayerTimetables.length > 0 && !!prayerData.today;
  }, [afterPrayerTimetables, prayerData.today]);

  const effectiveAfterPrayerOverlayPrayer =
    afterPrayerChanges.activePrayer ?? queuedAfterCriticalPrayer;

  // Derive critical playlist from PSM (verified) or scheduler (fast path)
  const derivedCritical = useMemo(() => {
    // PSM is the primary source
    if (prayerState.hasCriticalPlaylist && prayerState.activeCriticalPlaylist) {
      return {
        hasCritical: true,
        playlist: prayerState.activeCriticalPlaylist,
      };
    }
    // Fast path: check scheduler directly for any active PrayerBased playlist
    // This eliminates the multi-render-cycle delay waiting for PSM
    const prayerBasedActive = schedulerResult?.activePlaylists?.find(
      (ap: any) => {
        const playlist = ap.playlist;
        if (!playlist) return false;
        if (playlist.schedule?.type !== ScheduleType.PrayerBased) return false;

        return playlist.prayerDisplayMode !== "RespectLayout";
      },
    );
    if (prayerBasedActive?.playlist) {
      return { hasCritical: true, playlist: prayerBasedActive.playlist };
    }
    return { hasCritical: false, playlist: null };
  }, [
    prayerState.hasCriticalPlaylist,
    prayerState.activeCriticalPlaylist,
    schedulerResult?.activePlaylists,
  ]);

  // ── After-Prayer Salah Changes: sequencing with critical playlists ──
  // When both afterPrayerChanges and a critical (after-prayer) playlist are active,
  // we need to sequence them based on the user's position preference.
  // "before" = show salah changes overlay first, then let critical playlist play
  // "after" = let critical playlist play first, then show overlay

  // Determine if the after-prayer overlay should show RIGHT NOW
  const showAfterPrayerOverlay = useMemo(() => {
    if (!afterPrayerChanges.isActive || !afterPrayerChanges.activePrayer)
      return false;
    if (!canRenderAfterPrayerOverlay) return false;
    if (afterPrayerOverlayDismissed) return false;
    if (prayerState.isScreenBlank || prayerState.isCountdownActive)
      return false;

    const position = screenData?.salahChangesAfterPrayerPosition ?? "before";

    if (!derivedCritical.hasCritical) {
      // No after-prayer playlist — show overlay standalone
      return true;
    }

    // There IS a critical (after-prayer) playlist active
    if (position === "before") {
      // Show overlay FIRST (before the critical playlist)
      return true;
    }

    // position === "after": overlay shows AFTER critical playlist completes
    return false;
  }, [
    afterPrayerChanges.isActive,
    afterPrayerChanges.activePrayer,
    canRenderAfterPrayerOverlay,
    afterPrayerOverlayDismissed,
    prayerState.isScreenBlank,
    prayerState.isCountdownActive,
    derivedCritical.hasCritical,
    screenData?.salahChangesAfterPrayerPosition,
  ]);

  const showAfterPrayerOverlayPostCritical = useMemo(() => {
    if (!afterPrayerChanges.isActive && !queuedAfterCriticalOverlay)
      return false;
    if (!effectiveAfterPrayerOverlayPrayer) return false;
    if (!canRenderAfterPrayerOverlay) return false;
    if (afterPrayerOverlayDismissed) return false;
    if (prayerState.isScreenBlank || prayerState.isCountdownActive)
      return false;

    const position = screenData?.salahChangesAfterPrayerPosition ?? "before";
    if (position !== "after") return false;

    // Show after critical playlist has completed during this prayer's window
    return (
      (criticalCompletedDuringAfterPrayer || queuedAfterCriticalOverlay) &&
      !derivedCritical.hasCritical
    );
  }, [
    afterPrayerChanges.isActive,
    effectiveAfterPrayerOverlayPrayer,
    canRenderAfterPrayerOverlay,
    afterPrayerOverlayDismissed,
    prayerState.isScreenBlank,
    prayerState.isCountdownActive,
    screenData?.salahChangesAfterPrayerPosition,
    criticalCompletedDuringAfterPrayer,
    queuedAfterCriticalOverlay,
    derivedCritical.hasCritical,
  ]);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    const signature = [
      afterPrayerChanges.isActive ? "1" : "0",
      afterPrayerChanges.activePrayer || "none",
      showAfterPrayerOverlay ? "1" : "0",
      showAfterPrayerOverlayPostCritical ? "1" : "0",
      derivedCritical.hasCritical ? "1" : "0",
      derivedCritical.playlist?.id || "none",
      playLatchedBeforeCritical ? "1" : "0",
      latchedBeforeCriticalPlaylist?.id || "none",
      queuedAfterCriticalOverlay ? "1" : "0",
      queuedAfterCriticalPrayer || "none",
      screenData?.salahChangesAfterPrayerPosition || "before",
    ].join("|");

    if (signature === afterPrayerDebugSignatureRef.current) {
      return;
    }

    afterPrayerDebugSignatureRef.current = signature;

    console.log("🧭 [DisplayController] After-prayer sequencing", {
      active: afterPrayerChanges.isActive,
      activePrayer: afterPrayerChanges.activePrayer,
      position: screenData?.salahChangesAfterPrayerPosition || "before",
      showOverlayBefore: showAfterPrayerOverlay,
      showOverlayAfter: showAfterPrayerOverlayPostCritical,
      derivedCriticalActive: derivedCritical.hasCritical,
      derivedCriticalPlaylistId: derivedCritical.playlist?.id || null,
      latchedPlaybackActive: playLatchedBeforeCritical,
      latchedPlaylistId: latchedBeforeCriticalPlaylist?.id || null,
      queuedOverlayActive: queuedAfterCriticalOverlay,
      queuedOverlayPrayer: queuedAfterCriticalPrayer,
    });
  }, [
    afterPrayerChanges.isActive,
    afterPrayerChanges.activePrayer,
    showAfterPrayerOverlay,
    showAfterPrayerOverlayPostCritical,
    derivedCritical.hasCritical,
    derivedCritical.playlist,
    playLatchedBeforeCritical,
    latchedBeforeCriticalPlaylist,
    queuedAfterCriticalOverlay,
    queuedAfterCriticalPrayer,
    screenData?.salahChangesAfterPrayerPosition,
  ]);

  useEffect(() => {
    const position = screenData?.salahChangesAfterPrayerPosition ?? "before";
    if (position !== "before") return;
    if (!showAfterPrayerOverlay) return;
    if (!afterPrayerChanges.activePrayer) return;
    if (!derivedCritical.playlist) return;
    if (playLatchedBeforeCritical) return;

    setLatchedBeforeCriticalPlaylist(derivedCritical.playlist);
    setPlayLatchedBeforeCritical(true);
    latchedBeforeCriticalForRef.current = afterPrayerChanges.activePrayer;

    if (import.meta.env.DEV) {
      console.log("▶️ [DisplayController] Latched before-mode critical", {
        prayer: afterPrayerChanges.activePrayer,
        playlistId: derivedCritical.playlist.id,
        playlistName: derivedCritical.playlist.name,
        reason: "overlay-visible",
      });
    }
  }, [
    showAfterPrayerOverlay,
    screenData?.salahChangesAfterPrayerPosition,
    afterPrayerChanges.activePrayer,
    derivedCritical.playlist,
    playLatchedBeforeCritical,
  ]);

  useEffect(() => {
    const position = screenData?.salahChangesAfterPrayerPosition ?? "before";
    if (position !== "after") return;
    if (!afterPrayerChanges.isActive || !afterPrayerChanges.activePrayer)
      return;
    if (!canRenderAfterPrayerOverlay) return;
    if (!derivedCritical.playlist) return;
    if (
      queuedAfterCriticalOverlay &&
      queuedAfterCriticalForRef.current === afterPrayerChanges.activePrayer
    ) {
      return;
    }

    setQueuedAfterCriticalOverlay(true);
    setQueuedAfterCriticalPrayer(afterPrayerChanges.activePrayer);
    queuedAfterCriticalForRef.current = afterPrayerChanges.activePrayer;

    if (import.meta.env.DEV) {
      console.log("⏳ [DisplayController] Queued after-mode overlay", {
        prayer: afterPrayerChanges.activePrayer,
        playlistId: derivedCritical.playlist.id,
        playlistName: derivedCritical.playlist.name,
      });
    }
  }, [
    screenData?.salahChangesAfterPrayerPosition,
    afterPrayerChanges.isActive,
    afterPrayerChanges.activePrayer,
    canRenderAfterPrayerOverlay,
    derivedCritical.playlist,
    queuedAfterCriticalOverlay,
  ]);

  const handleAfterPrayerOverlayComplete = useCallback(() => {
    const position = screenData?.salahChangesAfterPrayerPosition ?? "before";

    if (import.meta.env.DEV) {
      console.log("🎬 [DisplayController] Overlay completed", {
        position,
        activePrayer: afterPrayerChanges.activePrayer,
        derivedCriticalActive: derivedCritical.hasCritical,
        derivedCriticalPlaylistId: derivedCritical.playlist?.id || null,
      });
    }

    setAfterPrayerOverlayDismissed(true);
    afterPrayerOverlayDismissedForRef.current =
      effectiveAfterPrayerOverlayPrayer;
    setQueuedAfterCriticalOverlay(false);
    setQueuedAfterCriticalPrayer(null);
    queuedAfterCriticalForRef.current = null;
  }, [
    effectiveAfterPrayerOverlayPrayer,
    screenData?.salahChangesAfterPrayerPosition,
    afterPrayerChanges.activePrayer,
    derivedCritical.hasCritical,
    derivedCritical.playlist,
  ]);

  const handleCriticalCompleteWithAfterPrayer = useCallback(() => {
    if (playLatchedBeforeCritical) {
      if (import.meta.env.DEV) {
        console.log("✅ [DisplayController] Latched critical completed", {
          prayer: afterPrayerChanges.activePrayer,
          playlistId: latchedBeforeCriticalPlaylist?.id || null,
        });
      }

      setPlayLatchedBeforeCritical(false);
      setLatchedBeforeCriticalPlaylist(null);
      latchedBeforeCriticalForRef.current = null;
      setCurrentPage("prayer");
      return;
    }

    // If we're in an after-prayer window and position is "after", flag it
    if (afterPrayerChanges.isActive && effectiveAfterPrayerOverlayPrayer) {
      const position = screenData?.salahChangesAfterPrayerPosition ?? "before";
      if (position === "after") {
        setCriticalCompletedDuringAfterPrayer(true);
        criticalCompletedForRef.current = effectiveAfterPrayerOverlayPrayer;
      }
    }
    setCurrentPage("prayer");
  }, [
    afterPrayerChanges.isActive,
    effectiveAfterPrayerOverlayPrayer,
    latchedBeforeCriticalPlaylist,
    playLatchedBeforeCritical,
    screenData?.salahChangesAfterPrayerPosition,
  ]);

  const activeFullScreenUrgentTakeoverPlaylist = useMemo(() => {
    const now = new Date();
    const activeTakeover = schedulerResult?.activePlaylists?.find((ap: any) => {
      const playlist = ap.playlist;
      if (!playlist) return false;
      return isUrgentTakeoverFullScreenActive(playlist, now);
    });

    return activeTakeover?.playlist || null;
  }, [schedulerResult?.activePlaylists]);

  const hasFullScreenUrgentTakeover =
    activeFullScreenUrgentTakeoverPlaylist !== null;

  const hasFullScreenUrgentInject = useMemo(() => {
    const now = new Date();
    return !!schedulerResult?.activePlaylists?.some((ap: any) => {
      const playlist = ap.playlist;
      if (!playlist) return false;
      return isUrgentInjectFullScreenActive(playlist, now);
    });
  }, [schedulerResult?.activePlaylists]);

  const hasSlideshowOnlySuppression = useMemo(() => {
    return !!schedulerResult?.activePlaylists?.some((ap: any) => {
      const playlist = ap.playlist;
      if (!playlist) return false;
      return (
        playlist.isExclusiveWhileActive === true &&
        playlist.isSlideshowSuppressingMode === true
      );
    });
  }, [schedulerResult?.activePlaylists]);

  // Azure Best Practice: Use refs for timers to properly handle cleanup
  const slideshowTimerRef = useRef<NodeJS.Timeout | null>(null);
  const prayerTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up timers when component unmounts
  useEffect(() => {
    return () => {
      if (slideshowTimerRef.current) clearTimeout(slideshowTimerRef.current);
      if (prayerTimerRef.current) clearTimeout(prayerTimerRef.current);
    };
  }, []);

  // Optimize to prevent multiple fetches by using useRef to track data loading state
  const dataFetchingRef = useRef({
    isLoadingScreenData: false,
    isLoadingPrayerTimes: false,
    lastScreenDataFetch: 0,
  });

  // Azure Best Practice: Define reusable data update function outside useEffects
  const handleDataUpdate = useCallback(async () => {
    if (!screenData?.masjidId) return;

    // Add debouncing logic - avoid updating too frequently
    const now = Date.now();
    if (dataFetchingRef.current.isLoadingPrayerTimes) {
      return;
    }

    dataFetchingRef.current.isLoadingPrayerTimes = true;

    // Get dates for today and tomorrow
    const nowDate = new Date();
    const year = nowDate.getFullYear().toString();
    const month = (nowDate.getMonth() + 1).toString();
    const today = nowDate.getDate().toString();

    // Calculate tomorrow's date
    const tomorrow = new Date(nowDate);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowDay = tomorrow.getDate().toString();
    const tomorrowMonth = (tomorrow.getMonth() + 1).toString();
    const tomorrowYear = tomorrow.getFullYear().toString();

    try {
      // 1. Get today's prayer times from current month
      const currentMonthTimetable = await timetableDB.getTimetable(
        year,
        month,
        screenData.masjidId.toString(),
      );

      if (currentMonthTimetable) {
        // Use the date string format that fetchPrayerTimes expects (YYYY-MM-DD)
        const todayDateStr = `${year}-${month.padStart(
          2,
          "0",
        )}-${today.padStart(2, "0")}`;
        const todayPrayerTimes = await fetchPrayerTimes(
          todayDateStr,
          screenData.masjidId,
        );

        console.log(
          "Updating prayerTimes state with today's transformed data:",
          todayPrayerTimes,
        );
        setPrayerData((prev) => ({ ...prev, today: todayPrayerTimes }));

        // 2. Check if tomorrow is in the current month
        if (month === tomorrowMonth && year === tomorrowYear) {
          const tomorrowDateStr = `${year}-${month.padStart(
            2,
            "0",
          )}-${tomorrowDay.padStart(2, "0")}`;
          const tomorrowPrayerTimes = await fetchPrayerTimes(
            tomorrowDateStr,
            screenData.masjidId,
          );
          console.log(
            "Updating tomorrow's prayer times from current month:",
            tomorrowPrayerTimes,
          );
          setPrayerData((prev) => ({ ...prev, tomorrow: tomorrowPrayerTimes }));
        }
        // 3. If tomorrow is in a different month, load that timetable
        else {
          const nextMonthTimetable = await timetableDB.getTimetable(
            tomorrowYear,
            tomorrowMonth,
            screenData.masjidId.toString(),
          );

          if (nextMonthTimetable) {
            const tomorrowDateStr = `${tomorrowYear}-${tomorrowMonth.padStart(
              2,
              "0",
            )}-${tomorrowDay.padStart(2, "0")}`;
            const tomorrowPrayerTimes = await fetchPrayerTimes(
              tomorrowDateStr,
              screenData.masjidId,
            );
            console.log(
              "Updating tomorrow's prayer times from next month:",
              tomorrowPrayerTimes,
            );
            setPrayerData((prev) => ({
              ...prev,
              tomorrow: tomorrowPrayerTimes,
            }));
          } else {
            setPrayerData((prev) => ({ ...prev, tomorrow: null }));
          }
        }
      } else {
        // No cached timetable — keep whatever is showing; nulling here would
        // flip a rendering screen onto the error gate. But never a past day's
        // times as today's: after a month change with the new month missing,
        // show the empty day (--:--) instead
        console.warn(
          "No cached timetable for current month; keeping existing prayer data",
        );
        const todayPrayerTimes = await fetchPrayerTimes(
          `${year}-${month.padStart(2, "0")}-${today.padStart(2, "0")}`,
          screenData.masjidId,
        );
        setPrayerData((prev) =>
          prev.today && Number(prev.today.Date) !== Number(today)
            ? { ...prev, today: todayPrayerTimes, tomorrow: null }
            : prev,
        );
      }
    } catch (error) {
      console.error("Error updating prayer times from IndexedDB:", error);
      // Azure Best Practice: Degrade gracefully on error, keep existing data
    } finally {
      dataFetchingRef.current.isLoadingPrayerTimes = false;
    }
  }, [screenData?.masjidId]); // Only recreate when masjidId changes

  // Slideshow timer management
  useEffect(() => {
    // Only set up slideshow cycling if we have screen data and are in prayer view
    if (!screenData || currentPage !== "prayer" || !prayerData.today) {
      return;
    }

    // Clear any existing timer
    if (slideshowTimerRef.current) {
      clearTimeout(slideshowTimerRef.current);
      slideshowTimerRef.current = null;
    }

    // Don't set up slideshow if we should pause content, countdown is active, or critical playlist is active
    if (
      prayerState.shouldPauseContent ||
      prayerState.isCountdownActive ||
      prayerState.hasCriticalPlaylist
    ) {
      return;
    }

    if (hasSlideshowOnlySuppression) {
      return;
    }

    // NEW: Check if transitions are disabled due to playlist display components
    if (transitionsDisabled) {
      return;
    }

    // Azure Best Practice: Check both regular playlists and special features
    const hasPlaylists =
      (screenData.playlists && screenData.playlists.length > 0) ||
      upcomingSalahChangesEligible ||
      screenData.showUpcomingEvents ||
      hasLabbaik;

    if (hasPlaylists) {
      // Get duration to show prayer page (default 30s if not specified)
      const prayerPageDuration = getPrayerPageDuration(screenData) * 1000;

      // ✅ Capture schedulerResult at the time of timer setup
      const schedulerResultSnapshot = schedulerResult;

      // Schedule transition to slideshow
      slideshowTimerRef.current = setTimeout(() => {
        // Pass schedulerResult snapshot to the check
        const hasItems =
          hasLabbaik ||
          hasEligibleSlideshowItems(
            screenData,
            schedulerResultSnapshot,
            upcomingSalahChangesEligible,
          );

        if (hasItems) {
          setCurrentPage("slideshow");
        }
      }, prayerPageDuration);
    }

    return () => {
      if (slideshowTimerRef.current) {
        clearTimeout(slideshowTimerRef.current);
        slideshowTimerRef.current = null;
      }
    };
  }, [
    screenData,
    currentPage,
    prayerData.today,
    prayerState.shouldPauseContent,
    prayerState.isCountdownActive,
    prayerState.hasCriticalPlaylist, // Add critical playlist dependency
    hasSlideshowOnlySuppression,
    transitionsDisabled,
    upcomingSalahChangesEligible,
    hasLabbaik,
  ]);

  // Handle slideshow completion
  const handleSlideshowComplete = useCallback(() => {
    if (hasSlideshowOnlySuppression) {
      setCurrentPage("slideshow");
      return;
    }
    console.log("Slideshow completed, returning to prayer page");
    setCurrentPage("prayer");
  }, [hasSlideshowOnlySuppression]);

  const refreshScreenData = useCallback(async () => {
    try {
      const masjidId = getMasjidId();
      if (!masjidId) {
        console.error("Missing masjid ID for screen data refresh");
        return;
      }
      const data = await fetchScreenData(masjidId, screenCode);
      setScreenData(data);
    } catch (err) {
      console.error("Error refreshing screen data:", err);
    }
  }, [screenCode]);

  // Warm the service worker cache with every playlist asset so slides render
  // offline; runs on each screen-data refresh (cache hits are free)
  useEffect(() => {
    if (!screenData) return;
    prefetchScreenAssets(screenData).catch(() => {});
  }, [screenData]);

  // Prayer sync setup effect
  useEffect(() => {
    if (!screenData?.masjidId) return;

    let syncController: { stop: () => void } | null = null;
    let isMounted = true;

    startPrayerTimeSync(
      screenData.masjidId,
      handleDataUpdate, // Now using the reusable function
      screenData.timesRefreshInterval,
      screenData.showUpcomingEvents,
      refreshScreenData,
    ).then((controller) => {
      if (isMounted) {
        syncController = controller;
      } else if (controller && typeof controller.stop === "function") {
        controller.stop();
      }
    });

    return () => {
      isMounted = false;
      if (syncController && typeof syncController.stop === "function") {
        syncController.stop();
      }
    };
  }, [
    screenData?.masjidId,
    screenData?.timesRefreshInterval,
    screenData?.showUpcomingEvents,
    handleDataUpdate,
    refreshScreenData,
  ]);

  // The sync above can be an hour apart, which left yesterday's times and Hijri date up after
  // midnight. Load the new day's times within a minute of the date changing.
  useEffect(() => {
    if (!screenData?.masjidId) return;
    let day = new Date().toDateString();
    const timer = window.setInterval(() => {
      const today = new Date().toDateString();
      if (today === day) return;
      day = today;
      handleDataUpdate();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [screenData?.masjidId, handleDataUpdate]);

  useEffect(() => {
    if (!screenCode) return;

    // Avoid duplicate fetches
    if (dataFetchingRef.current.isLoadingScreenData) {
      console.log("Screen data fetch already in progress, skipping");
      return;
    }

    // Add time-based throttling
    const now = Date.now();
    if (now - dataFetchingRef.current.lastScreenDataFetch < 10000) {
      // 10 seconds
      console.log("Screen data was recently fetched, skipping");
      return;
    }

    setLoading(true);
    dataFetchingRef.current.isLoadingScreenData = true;
    dataFetchingRef.current.lastScreenDataFetch = now;

    const masjidId = getMasjidId();
    if (!masjidId) {
      setError("Missing masjid ID. Please re-authenticate.");
      setLoading(false);
      return;
    }

    console.log("[DisplayController] Initial load started", {
      screenCode,
      masjidId,
    });

    fetchScreenData(masjidId, screenCode)
      .then((data) => {
        console.log("[DisplayController] Screen data loaded", {
          screenCode,
          resolvedMasjidId: data.masjidId,
          hasLayoutData: !!data.layoutData,
        });
        setScreenData(data);

        // Get today's date
        const today = new Date();
        const currentDate = today.toISOString().split("T")[0];

        // Calculate tomorrow's date
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowDate = tomorrow.toISOString().split("T")[0];

        // Fetch both today's and tomorrow's prayer times in parallel
        return Promise.all([
          fetchPrayerTimes(currentDate, data.masjidId),
          fetchPrayerTimes(tomorrowDate, data.masjidId),
        ]);
      })
      .then(([todayTimes, tomorrowTimes]) => {
        const fallbackToday = isMissingTimetableFallback(todayTimes);
        const fallbackTomorrow = isMissingTimetableFallback(tomorrowTimes);

        console.log("[DisplayController] Prayer times loaded", {
          screenCode,
          fallbackToday,
          fallbackTomorrow,
          mode:
            fallbackToday || fallbackTomorrow
              ? "missing-month-fallback"
              : "normal",
        });

        setPrayerData((prev) => ({ ...prev, today: todayTimes }));
        setPrayerData((prev) => ({ ...prev, tomorrow: tomorrowTimes })); // Add this state variable
        setLoading(false);
      })
      .catch((err) => {
        console.error("[DisplayController] Initial load failed", {
          screenCode,
          error: err?.message || String(err),
          online: navigator.onLine,
        });

        // A kiosk must never dead-end: everything retries automatically except
        // an explicit 4xx from the screen API (deleted/invalid screen — a
        // human must fix the config via the always-present AuthMenu)
        const isConfigError =
          err?.response?.status >= 400 && err.response.status < 500;

        if (isConfigError) {
          setError(err.message || "Failed to load screen data");
          setLoading(false);
          return;
        }

        setError(
          "Connecting... prayer times will appear when network is available",
        );
        setLastLoadError(err?.message || String(err));
        setLoading(false);

        const clearRetries = () => {
          const ref = dataFetchingRef.current as any;
          if (ref._retryOnOnline) {
            window.removeEventListener("online", ref._retryOnOnline);
            delete ref._retryOnOnline;
          }
          if (ref._retryInterval) {
            clearInterval(ref._retryInterval);
            delete ref._retryInterval;
          }
        };

        const retryInitialLoad = () => {
          console.log("Retrying initial screen load");
          dataFetchingRef.current.isLoadingScreenData = false;
          dataFetchingRef.current.lastScreenDataFetch = 0;
          fetchScreenData(masjidId!, screenCode)
            .then((data) => {
              setScreenData(data);
              const today = new Date();
              const currentDate = today.toISOString().split("T")[0];
              const tomorrow = new Date(today);
              tomorrow.setDate(tomorrow.getDate() + 1);
              const tomorrowDate = tomorrow.toISOString().split("T")[0];
              return Promise.all([
                fetchPrayerTimes(currentDate, data.masjidId),
                fetchPrayerTimes(tomorrowDate, data.masjidId),
              ]);
            })
            .then(([todayTimes, tomorrowTimes]) => {
              clearRetries();
              setError(null);
              setPrayerData((prev) => ({ ...prev, today: todayTimes }));
              setPrayerData((prev) => ({ ...prev, tomorrow: tomorrowTimes }));
              setLoading(false);
            })
            .catch((retryErr) => {
              // Keep waiting — the interval/online listener will try again
              console.warn("Retry failed:", retryErr?.message || retryErr);
              setLastLoadError(retryErr?.message || String(retryErr));
            });
        };

        window.addEventListener("online", retryInitialLoad);
        (dataFetchingRef.current as any)._retryOnOnline = retryInitialLoad;
        // The online event never fires on LAN-without-internet, so also poll
        (dataFetchingRef.current as any)._retryInterval = window.setInterval(
          retryInitialLoad,
          60000,
        );
      })
      .finally(() => {
        dataFetchingRef.current.isLoadingScreenData = false;
      });

    return () => {
      // Clean up any retry listener/interval registered by a failed load
      const ref = dataFetchingRef.current as any;
      const handler = ref._retryOnOnline;
      if (handler) {
        window.removeEventListener("online", handler);
        delete ref._retryOnOnline;
      }
      if (ref._retryInterval) {
        clearInterval(ref._retryInterval);
        delete ref._retryInterval;
      }
    };
  }, [screenCode]);

  useEffect(() => {
    if (!screenCode || !screenData?.timesRefreshInterval) return;

    // Refresh once on mount, then poll on the screen's configured interval
    refreshScreenData();

    const interval = setInterval(
      refreshScreenData,
      screenData.timesRefreshInterval * 60 * 1000,
    );
    // Back online: posters and notices catch up now rather than at the next poll
    window.addEventListener("online", refreshScreenData);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", refreshScreenData);
    };
  }, [screenCode, screenData?.timesRefreshInterval, refreshScreenData]);

  useEffect(() => {
    if (!screenData?.offlineRoutingDiagnostics) {
      return;
    }

    console.log(
      "[OfflineRoutingDiagnostics]",
      screenData.offlineRoutingDiagnostics,
    );
  }, [screenData?.offlineRoutingDiagnostics]);

  // Helper function to detect if layout contains playlist display components
  const hasPlaylistDisplayComponents = useCallback(
    (layoutData: any): boolean => {
      if (!layoutData || (!layoutData.Components && !layoutData.components)) {
        return false;
      }

      const components = layoutData.Components || layoutData.components;

      const playlistComponents = components.filter((component: any) => {
        const id = component.Id || component.id;
        const playlistDisplay =
          component.playlistDisplay || component.PlaylistDisplay;

        // Check if component has playlist display settings or ID pattern
        return !!(
          playlistDisplay ||
          (id && id.toLowerCase().includes("playlist"))
        );
      });

      console.log("🎬 Detected playlist display components:", {
        total: components.length,
        playlistComponents: playlistComponents.length,
        playlistComponentIds: playlistComponents.map((c: any) => c.Id || c.id),
      });

      return playlistComponents.length > 0;
    },
    [],
  );

  // Service worker message handler effect
  useEffect(() => {
    // Azure Best Practice: Update UI when prayer times change from service worker
    const handlePrayerTimesUpdate = async (event: Event) => {
      try {
        const customEvent = event as PrayerTimesUpdateEvent;
        console.log(
          "DisplayController received prayer times update",
          customEvent.detail,
        );

        // Refresh prayer times data from IndexedDB
        await handleDataUpdate();
      } catch (error) {
        console.error("Error handling prayer times update event:", error);
      }
    };

    // Add event listener as a typed CustomEvent
    window.addEventListener("prayerTimesUpdated", handlePrayerTimesUpdate);

    // Clean up
    return () => {
      window.removeEventListener("prayerTimesUpdated", handlePrayerTimesUpdate);
    };
  }, [handleDataUpdate]); // Add handleDataUpdate as dependency

  // Add this effect to explicitly handle page transitions with proper priority
  useEffect(() => {
    // Skip if loading or missing data
    if (!screenData || !prayerData.today) return;

    // Priority 1: Blank screen (highest priority)
    if (prayerState.isScreenBlank) {
      setCurrentPage("prayer"); // Prayer page handles blank state
      return;
    }

    // Priority 2: Countdown timer (prayer time is sacred — always wins over playlists)
    if (prayerState.isCountdownActive) {
      const countdownPage: PageState = screenData.showFullscreenCountdown
        ? "countdown"
        : "prayer";

      setCurrentPage(countdownPage);
      return;
    }

    // Priority 3: Critical/PrayerBased playlists (uses fast-path scheduler check)
    if (derivedCritical.hasCritical) {
      setCurrentPage("slideshow");
      return;
    }

    // Priority 4: Full-screen urgent takeover
    if (hasFullScreenUrgentTakeover) {
      setCurrentPage("slideshow");
      return;
    }

    // Priority 5: Full-screen urgent inject
    if (hasFullScreenUrgentInject) {
      setCurrentPage("slideshow");
      return;
    }

    // Priority 6: Exclusive slideshow-only suppression (not fullscreen takeover)
    if (hasSlideshowOnlySuppression) {
      setCurrentPage("slideshow");
      return;
    }

    // Lower priorities follow existing logic...
  }, [
    schedulerResult,
    prayerState,
    screenData,
    prayerData.today,
    derivedCritical,
    hasFullScreenUrgentTakeover,
    activeFullScreenUrgentTakeoverPlaylist,
    hasFullScreenUrgentInject,
    hasSlideshowOnlySuppression,
  ]);

  // Fallback guard: if critical just ended while on slideshow, normalize back to prayer page.
  useEffect(() => {
    const hadCritical = previousCriticalActiveRef.current;
    const hasCriticalNow = derivedCritical.hasCritical;

    if (
      hadCritical &&
      !hasCriticalNow &&
      currentPage === "slideshow" &&
      !prayerState.isCountdownActive &&
      !prayerState.isScreenBlank
    ) {
      if (evaluatePlaylists) {
        evaluatePlaylists();
      }

      setCurrentPage("prayer");
    }

    previousCriticalActiveRef.current = hasCriticalNow;
  }, [
    derivedCritical.hasCritical,
    currentPage,
    prayerState.isCountdownActive,
    prayerState.isScreenBlank,
    evaluatePlaylists,
  ]);

  useEffect(() => {
    if (currentPage === "countdown" && !prayerState.isCountdownActive) {
      setCurrentPage("prayer");
    }
  }, [currentPage, prayerState.isCountdownActive]);

  // Check for playlist display components and set transitions state
  useEffect(() => {
    if (!screenData || screenData.isResponsive || !screenData.layoutData) {
      setTransitionsDisabled(false);
      return;
    }

    try {
      const layoutData = JSON.parse(screenData.layoutData);
      const hasPlaylistComponents = hasPlaylistDisplayComponents(layoutData);
      setTransitionsDisabled(hasPlaylistComponents);

      if (hasPlaylistComponents) {
        console.log(
          "🎬 Playlist display components detected - transitions disabled",
        );
      } else {
        console.log("🎬 No playlist display components - transitions enabled");
      }
    } catch (error) {
      console.warn(
        "Failed to parse layout data for playlist component detection:",
        error,
      );
      setTransitionsDisabled(false);
    }
  }, [screenData?.layoutData, hasPlaylistDisplayComponents]);

  // Auto-return to prayer page when playlists are empty (but not during critical playlist display)
  useEffect(() => {
    // Only check when we're on slideshow page and not during countdown or critical playlist
    if (
      currentPage !== "slideshow" ||
      prayerState.isCountdownActive ||
      derivedCritical.hasCritical ||
      hasFullScreenUrgentTakeover
    ) {
      return;
    }

    // Check if there are any eligible items to display
    const hasItems =
      hasLabbaik ||
      (screenData
        ? hasEligibleSlideshowItems(
            screenData,
            schedulerResult,
            upcomingSalahChangesEligible,
          )
        : false);

    if (!hasItems) {
      if (hasSlideshowOnlySuppression) {
        return;
      }
      console.log(
        "🚨 [DisplayController] No slideshow items to display, returning to prayer page",
      );
      setCurrentPage("prayer");
    }
  }, [
    currentPage,
    prayerState.isCountdownActive,
    derivedCritical.hasCritical,
    hasFullScreenUrgentTakeover,
    hasSlideshowOnlySuppression,
    screenData,
    schedulerResult,
    upcomingSalahChangesEligible,
    hasLabbaik,
  ]);

  // Handle changes availability
  const handleChangesAvailable = useCallback(
    (available: boolean) => {
      setHasChanges(available);

      if (hasSlideshowOnlySuppression) {
        return;
      }

      if (available && currentPage === "prayer") {
        // Schedule change to the changes page after 30 seconds
        const changeTimer = setTimeout(() => {
          setCurrentPage("changes");

          // Return to prayer page after 2 minutes
          setTimeout(
            () => {
              setCurrentPage("prayer");
            },
            2 * 60 * 1000,
          );
        }, 30 * 1000);

        return () => clearTimeout(changeTimer);
      }
    },
    [currentPage, hasSlideshowOnlySuppression],
  );

  // Add a new function to fetch the previous Friday's prayer times
  const fetchPreviousFridayTimes = useCallback(async () => {
    if (!screenData?.masjidId) return;

    try {
      // Get the date of the previous Friday
      const today = new Date();
      let daysSinceLastFriday = today.getDay() - 5;
      if (daysSinceLastFriday < 0) daysSinceLastFriday += 7;
      // If today is Friday (0 days since Friday), we want LAST week's Friday
      if (daysSinceLastFriday === 0) daysSinceLastFriday = 7;

      const lastFriday = new Date(today);
      lastFriday.setDate(today.getDate() - daysSinceLastFriday);
      const lastFridayDate = lastFriday.toISOString().split("T")[0];

      // Fetch the prayer times for the previous Friday
      const previousFridayTimes = await fetchPrayerTimes(
        lastFridayDate,
        screenData.masjidId,
      );

      if (previousFridayTimes) {
        setPrayerData((prev) => ({
          ...prev,
          prevFridayJummah1: previousFridayTimes.Jummah1 || null,
          prevFridayJummah2: previousFridayTimes.Jummah2 || null,
          prevFridayJummah3: previousFridayTimes.Jummah3 || null,
          prevFridayJummah4: previousFridayTimes.Jummah4 || null,
        }));
      }
    } catch (error) {
      console.error(`Error fetching previous Friday times:`, error);
    }
  }, [screenData?.masjidId]);

  // Call this function when prayer times are loaded
  useEffect(() => {
    if (prayerData.today) {
      fetchPreviousFridayTimes();
    }
  }, [prayerData.today, fetchPreviousFridayTimes]);

  // Loading state
  if (loading) {
    return (
      <div className="screen-container loading-screen">
        <LoadingSpinner />
        {authMenu}
      </div>
    );
  }

  // Error state
  if (error || !screenData || !prayerData.today) {
    const isWaitingForNetwork = error?.startsWith("Connecting...");
    return (
      <div className="screen-container error-screen">
        {isWaitingForNetwork ? (
          <>
            <LoadingSpinner />
            <p style={{ color: "#fff", marginTop: "1rem", fontSize: "1.2rem" }}>
              {error}
            </p>
            {/* Shown so a customer's photo of a stuck screen tells us why */}
            <p style={{ color: "#888", fontSize: "0.8rem" }}>
              {navigator.userAgent.match(/Chrome\/\d+/)?.[0] ?? "Browser"} ·{" "}
              {lastLoadError}
            </p>
          </>
        ) : (
          <ErrorDisplay
            message={
              error || "Something went wrong. Failed to load screen data."
            }
            retryAction={() => window.location.reload()}
          />
        )}
        {authMenu}
      </div>
    );
  }

  // Parse layout data if present and not in responsive mode
  const parsedLayoutData =
    !screenData.isResponsive && screenData.layoutData
      ? JSON.parse(screenData.layoutData)
      : null;

  // Check if we should render custom database layout
  // This will render when layoutData exists and contains actual layout data (not just empty JSON)
  const hasDatabaseLayoutData =
    screenData.layoutData &&
    screenData.layoutData.trim() !== "" &&
    screenData.layoutData !== "{}";

  const shouldRenderDatabaseLayout = hasDatabaseLayoutData;
  const shouldRenderResponsiveLayout =
    !shouldRenderDatabaseLayout && screenData.isResponsive;

  // Determine which display mode to use
  const displayMode = screenData.displayMode || "slideshow";
  const effectiveDisplayMode = hasSlideshowOnlySuppression
    ? "slideshow"
    : displayMode;
  const filteredSlideshowScreenData: ScreenData = hasSlideshowOnlySuppression
    ? {
        ...screenData,
        displayMode: "slideshow" as const,
        showNextSalahChanges: false,
        showUpcomingEvents: false,
        showHomePageBetweenCycles: false,
      }
    : screenData;
  const shouldShowFullscreenCountdown =
    !!screenData.showFullscreenCountdown && prayerState.isCountdownActive;

  const nightModeActive = isNightModeActive(screenData, prayerData.today);
  const parsedNightModeIntensity = Number(screenData.nightModeIntensity);
  const nightModeIntensity = Number.isFinite(parsedNightModeIntensity)
    ? Math.min(100, Math.max(0, parsedNightModeIntensity))
    : 28;
  const nightModeOpacity = nightModeIntensity / 100;
  const rootClassName = "display-controller";
  const effectiveCriticalPlaylist = playLatchedBeforeCritical
    ? latchedBeforeCriticalPlaylist
    : derivedCritical.playlist;
  const hasEffectiveCritical =
    !!effectiveCriticalPlaylist &&
    (derivedCritical.hasCritical || playLatchedBeforeCritical);

  // Render appropriate view based on current page and screen type
  return (
    <div className={rootClassName}>
      {nightModeActive && (
        <div
          className="night-mode-overlay"
          style={{ opacity: nightModeOpacity }}
          aria-hidden="true"
        />
      )}

      {import.meta.env.DEV && screenData.offlineRoutingDiagnostics && (
        <div
          style={{
            position: "fixed",
            right: "12px",
            bottom: "12px",
            zIndex: 100000,
            fontSize: "11px",
            fontFamily: "monospace",
            color: "#fff",
            background: "rgba(0,0,0,0.7)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "6px",
            padding: "6px 8px",
            maxWidth: "380px",
            lineHeight: 1.3,
          }}
          title={JSON.stringify(screenData.offlineRoutingDiagnostics)}
        >
          <div>
            offline-routing: {screenData.offlineRoutingDiagnostics.reason}
          </div>
          {screenData.offlineRoutingDiagnostics.matchedRuleName && (
            <div>
              rule: {screenData.offlineRoutingDiagnostics.matchedRuleName}
            </div>
          )}
          {screenData.offlineRoutingDiagnostics.targetScreenCode && (
            <div>
              target: {screenData.offlineRoutingDiagnostics.targetScreenCode}
            </div>
          )}
        </div>
      )}

      {(connectionQuality === "offline" || connectionQuality === "slow") && (
        <div
          style={{
            position: "fixed",
            left: "12px",
            bottom: "12px",
            zIndex: 9999,
            opacity: 0.3,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="1" y1="1" x2="23" y2="23" />
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
            <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
            <line x1="12" y1="20" x2="12.01" y2="20" />
          </svg>
        </div>
      )}

      {/* AFTER-PRAYER SALAH CHANGES OVERLAY — renders above everything except blank/countdown */}
      {(showAfterPrayerOverlay || showAfterPrayerOverlayPostCritical) &&
        effectiveAfterPrayerOverlayPrayer &&
        canRenderAfterPrayerOverlay && (
          <AfterPrayerSalahChangesOverlay
            activePrayer={effectiveAfterPrayerOverlayPrayer}
            screenData={screenData}
            prayerTimes={prayerData.today}
            tomorrowPrayerTimes={prayerData.tomorrow}
            timetables={afterPrayerTimetables}
            duration={screenData.salahChangesAfterPrayerDuration ?? 30}
            onComplete={handleAfterPrayerOverlayComplete}
            prevFridayJummah1={prayerData.prevFridayJummah1}
            prevFridayJummah2={prayerData.prevFridayJummah2}
            prevFridayJummah3={prayerData.prevFridayJummah3}
            prevFridayJummah4={prayerData.prevFridayJummah4}
          />
        )}

      {/* CRITICAL PLAYLIST - HIGHEST PRIORITY: Always render when active */}
      {/* Suppressed when after-prayer overlay is showing in "before" position */}
      {hasEffectiveCritical &&
        !prayerState.isCountdownActive &&
        !prayerState.isScreenBlank &&
        !showAfterPrayerOverlay &&
        effectiveCriticalPlaylist && (
          <CriticalSlideshowPage
            key={`critical-${effectiveCriticalPlaylist.id}`}
            playlist={effectiveCriticalPlaylist}
            prayerTimes={prayerData.today}
            tomorrowPrayerTimes={prayerData.tomorrow}
            screenData={screenData}
            prayerState={prayerState}
            masjidId={screenData.masjidId}
            onCriticalComplete={handleCriticalCompleteWithAfterPrayer}
            runSingleCycle={playLatchedBeforeCritical}
          />
        )}

      {/* URGENT TAKEOVER LANE: Render only when no prayer-based critical playlist is active */}
      {!hasEffectiveCritical &&
        hasFullScreenUrgentTakeover &&
        !prayerState.isCountdownActive &&
        !prayerState.isScreenBlank &&
        activeFullScreenUrgentTakeoverPlaylist && (
          <CriticalSlideshowPage
            key={`takeover-${activeFullScreenUrgentTakeoverPlaylist.id}`}
            playlist={activeFullScreenUrgentTakeoverPlaylist}
            prayerTimes={prayerData.today}
            tomorrowPrayerTimes={prayerData.tomorrow}
            screenData={screenData}
            prayerState={prayerState}
            masjidId={screenData.masjidId}
            onCriticalComplete={() => {
              setCurrentPage("prayer");
            }}
          />
        )}

      {/* NORMAL CONTENT: Only render when no critical playlist is active */}
      {!hasEffectiveCritical && !hasFullScreenUrgentTakeover && (
        <>
          {/* PRAYER PAGE: Show when on prayer page or countdown is active */}
          {(currentPage === "prayer" ||
            (prayerState.isCountdownActive &&
              !shouldShowFullscreenCountdown)) &&
            (shouldRenderDatabaseLayout ? (
              <>
                <CustomDatabaseLayoutDisplay
                  layoutDataJson={screenData.layoutData || "{}"}
                  prayerTimes={prayerData.today}
                  tomorrowPrayerTimes={prayerData.tomorrow}
                  masjidId={screenData.masjidId}
                  screenName={screenData.name}
                  screenData={filteredSlideshowScreenData}
                  prayerState={prayerState}
                  prevFridayJummah1={prayerData.prevFridayJummah1}
                  prevFridayJummah2={prayerData.prevFridayJummah2}
                  prevFridayJummah3={prayerData.prevFridayJummah3}
                  prevFridayJummah4={prayerData.prevFridayJummah4}
                  flashingPrayer={flashSalahChanges.flashingPrayer}
                />
                {authMenu}
              </>
            ) : shouldRenderResponsiveLayout ? (
              <>
                <ResponsiveDisplay
                  prayerTimes={prayerData.today}
                  tomorrowPrayerTimes={prayerData.tomorrow}
                  screenConfig={{
                    masjidName: screenData.masjidName || "",
                    orientation: screenData.orientation,
                    templateId: screenData.backgroundTemplate ?? "",
                  }}
                  screenData={screenData}
                  onChangesAvailable={handleChangesAvailable}
                  hasChanges={hasChanges}
                  prayerState={prayerState}
                  flashingPrayer={flashSalahChanges.flashingPrayer}
                />
                {authMenu}
              </>
            ) : null)}

          {currentPage === "countdown" && shouldShowFullscreenCountdown && (
            <>
              <FullscreenCountdownTakeover
                prayerName={prayerState.countdownPrayer}
                seconds={prayerState.countdownSeconds}
                screenData={screenData}
              />
              {authMenu}
            </>
          )}

          {/* SLIDESHOW/NOTICEBOARD PAGE: Show based on displayMode */}
          {currentPage === "slideshow" && !prayerState.isCountdownActive && (
            <>
              {effectiveDisplayMode === "noticeboard" &&
              !hasFullScreenUrgentInject ? (
                <NoticeboardDisplay
                  items={allPlaylistItems}
                  screenData={screenData}
                  prayerTimes={prayerData.today}
                  tomorrowPrayerTimes={prayerData.tomorrow}
                  width={window.innerWidth}
                  height={window.innerHeight}
                  onComplete={handleSlideshowComplete}
                  prevFridayJummah1={prayerData.prevFridayJummah1}
                  prevFridayJummah2={prayerData.prevFridayJummah2}
                  prevFridayJummah3={prayerData.prevFridayJummah3}
                  prevFridayJummah4={prayerData.prevFridayJummah4}
                  prayerState={prayerState}
                  upcomingSalahChangesEligible={upcomingSalahChangesEligible}
                />
              ) : (
                <SlideshowPage
                  playlists={screenData.playlists || []}
                  prayerTimes={prayerData.today}
                  tomorrowPrayerTimes={prayerData.tomorrow}
                  masjidId={screenData.masjidId}
                  screenData={filteredSlideshowScreenData}
                  onComplete={handleSlideshowComplete}
                  forceContinuousLoop={hasSlideshowOnlySuppression}
                  prayerState={prayerState}
                  prevFridayJummah1={prayerData.prevFridayJummah1}
                  prevFridayJummah2={prayerData.prevFridayJummah2}
                  prevFridayJummah3={prayerData.prevFridayJummah3}
                  prevFridayJummah4={prayerData.prevFridayJummah4}
                  upcomingSalahChangesEligible={upcomingSalahChangesEligible}
                />
              )}
            </>
          )}

          {/* CHANGES PAGE */}
          {currentPage === "changes" && (
            <div className="changes-view">
              <h2>Upcoming Prayer Time Changes</h2>
              <p>These prayer times will change soon:</p>
              <PoweredBy className="homepage-footer" />
              {authMenu}
            </div>
          )}

          {/* EVENTS PAGE */}
          {currentPage === "events" && (
            <div className="events-view">
              <h2>Masjid Events</h2>
              <PoweredBy className="homepage-footer" />
              {authMenu}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Determines if there are any eligible slideshow items to display
 * Azure Best Practice: Check eligibility before transitioning to avoid UI flashes
 */
/**
 * Determines if there are any eligible slideshow items to display
 * Azure Best Practice: Check eligibility before transitioning to avoid UI flashes
 */
function hasEligibleSlideshowItems(
  screenData: ScreenData,
  schedulerResult?: any, // Add this parameter
  upcomingSalahChangesEligible = false,
): boolean {
  // Independent feature panels
  if (upcomingSalahChangesEligible) {
    return true;
  }

  if (screenData.showUpcomingEvents) {
    return true;
  }

  if (!schedulerResult?.activePlaylists) {
    return false;
  }

  // Check if any active playlist has content
  // (Virtual "Events" playlist with Id=-1 has been decommissioned)
  const hasContent = schedulerResult.activePlaylists.some((p: any) => {
    const playlist = p.playlist || p;
    return playlist.items && playlist.items.length > 0;
  });
  if (hasContent) {
    return true;
  }

  return false;
}
